<template>
    <div slider question :id="`question-${questionData.number}`">
        <questionheader :questionData="questionData"></questionheader>
        <div data-question-main>
            <div
                v-for="(sq, index) in questionData.subQuestions"
                :key="`sq-${index}`"
                :data-answer="questionData.type"
                :data-outerfield="hasOuterFields"
                @keyup.enter="submitQuestion"
            >
                   <div data-sq :id="`question-${questionData.number}_sq-${sq.number}`">
                        <template v-for="(answer, index) in sq.answers">
                        <div
                            v-if="answer.type === `Digit` && questionData.type === `Slider`"
                            data-slider-container
                            :disabled="isDisabled(sq)"
                            :key="index"
                        >
                            <div
                                class="scale"
                                data-range-container
                                :data-locked="!answer.isChecked"
                            >
                                <span data-only-screenreader id="slider-info-values">
                                    {{get.textResource(`rs_slider`)}}
                                    {{get.textResource(`rs_min`)}}
                                    {{ minValue }}
                                    {{get.textResource(`rs_max`)}}
                                    {{ restrictions.maxValue }}
                                </span>
                                <input
                                    type="range"
                                    class="slider"
                                    :id="`question-${questionData.number}_sq-${sq.number}_slider-${answer.number}`"
                                    :min="minValue"
                                    :required="restrictions.required"
                                    :max="restrictions.maxValue"
                                    aria-describedby="slider-info-values"
                                    v-model="answer.openField"
                                    @mousedown="rangeMouseDown(answer)"
                                    @touchstart="rangeMouseDown(answer)"
                                    @keydown.left="rangeMouseDown(answer)"
                                    @keydown.right="rangeMouseDown(answer)"
                                    @mouseup="rangeMouseUp"
                                    @touchend="rangeMouseDown"
                                    :disabled="isDisabled(sq)"
                                    :style="getRangeStyle(answer.openField)"
                                />
                                <div
                                    v-if="questionData.displayValue"
                                    class="slider-value"
                                    :style="bubblePositioning(answer)"
                                    aria-hidden="true"
                                >
                                    {{ answer.openField }}
                                </div>
                                <div
                                    class="ticks"
                                    data-slider-numbers
                                    aria-hidden="true"
                                >
                                    <template v-if="questionData.displayValue">
                                        <span
                                            v-for="tick in sliderTicks"
                                            :key="tick"
                                            data-number
                                            :style="{
                                                left: `${((tick - minValue) / (restrictions.maxValue - minValue)) * 100}%`
                                            }"
                                        >
                                            {{ tick }}
                                        </span>
                                    </template>
                                    <template v-else>
                                    <span data-number style="left: 0%">
                                        {{ minValue }}
                                    </span>

                                    <span data-number style="left: 100%">
                                        {{ restrictions.maxValue }}
                                    </span>
                                </template>
                                </div>
                                <div
                                    v-if="answer.labelLeft !== `` && answer.labelRight !== ``"
                                    class="labels"
                                    data-slider-labels
                                >
                                    <label
                                        v-if="sqLeftAnswer(sq).label !== ``"
                                        :id="`question-${questionData.number}_sq-${sq.number}_descr-left`"
                                        v-html="sqLeftAnswer(sq).label"
                                        data-left
                                    ></label>

                                    <label
                                        v-if="sqRightAnswer(sq).label !== ``"
                                        :id="`question-${questionData.number}_sq-${sq.number}_descr-right`"
                                        v-html="sqRightAnswer(sq).label"
                                        data-right
                                    ></label>
                                </div>
                            </div>
                        </div>
                        <div
                            v-else-if="answer.type === `OuterField` && questionData.type !== `SemanticDifferential`"
                            :key="`answer-${answer.id}`"
                            :disabled="!get.editingAllowed(questionData)"
                        >
                            <checkboxradio
                                :questionData="questionData"
                                :outerfield="true"
                                :answerData="answer"
                                :sqData="sq"
                                :showLabel="true"
                                type="checkbox"
                                @element-click="outerfieldClicked(sq)"
                                :disabled="!get.editingAllowed(questionData)"
                            />
                        </div>
                        <div
                            v-else-if="questionData.type === `SemanticDifferential` && answer.type !== `OuterField`"
                            data-semdiff
                            :key="`question-${questionData.number}_sq-${sq.number}_answer-${answer.id}`"
                            :disabled="isDisabled(sq)"
                            :style="{
                                width: `calc(100% / ${sqAnswers(sq).length})`
                            }"
                        >
                            <checkboxradio
                                :questionData="questionData"
                                :type="`radio`"
                                :name="`option-${sq.id}`"
                                :answerData="answer"
                                :sqData="sq"
                                @element-click="semdiffChanged(answer, sq)"
                                :disabled="isDisabled(sq)"
                            />
                        </div>
                    </template>
                </div>
                <div data-labels v-if="questionData.type === `SemanticDifferential`">
                    <div data-left-answer>
                       <label v-html="sq.answers[0].label"></label>
                    </div>
                    <div data-right-answer>
                        <label
                           v-html="sqOuterfield(sq) ? sq.answers[sq.answers.length -2].label : sq.answers[sq.answers.length -1].label"
                        ></label>
                    </div>
                </div>
                <div
                    v-if="questionData.type === `SemanticDifferential` && sqOuterfield(sq)"
                    data-outerfield-container
                >
                    <checkboxradio
                        :questionData="questionData"
                        :outerfield="true"
                        :answerData="sqOuterfield(sq)"
                        :showLabel="true"
                        :sqData="sq"
                        type="checkbox"
                        @element-click="outerfieldClicked(sq)"
                        :disabled="!get.editingAllowed(sq, false)"
                    />
                </div>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";

export default {
    name: `slidersemdiff-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],
    data: function () {
        return {
            rangeBeingUsedId: -1, // Used to determine which bulb should be shown when the user drags a slider
        };
    },
    computed: {
        sliderTicks() {
            const min = Number(this.minValue);
            const max = Number(this.restrictions.maxValue);

            // Show every number when range is small
            if (max - min <= 10) {
                const result = [];

                for (let i = min; i <= max; i++) {
                    result.push(i);
                }

                return result;
            }

            const targetTicks = 6;
            const rawStep = (max - min) / (targetTicks - 1);
            const step = this.niceStep(rawStep);

            const ticks = [min];
            let start = Math.ceil(min / step) * step;

            for (let v = start; v < max; v += step) {
                if (v !== min) ticks.push(v);
            }

            if (ticks[ticks.length - 1] !== max) {
                ticks.push(max);
            }

            return ticks;
        },
        answersGiven: function(){
            return this.questionData.subQuestions.filter((sq) => {
                //Either the slider or outerfield has to be checked for a subquestion to be answered
                return sq.answers.filter((answer) => answer.isChecked).length > 0;
            }).length;
        },
       // If there are outerfields the styling of the page should be slightly different
        hasOuterFields: function () {
            return this.questionData.subQuestions[0].answers.filter((answer) => answer.type === `OuterField`).length > 0;
        },
        // When a minimal value is not specified -1 will be returned from the back-end but it should be 0
        minValue: function () {
            return this.restrictions.minValue === -1 ? 0 : this.restrictions.minValue;
        },
    },
    methods: {
        outerfieldClicked: function (sq) {
            let outerfield = this.sqOuterfield(sq);

            outerfield.isChecked = !outerfield.isChecked;
        },
        // Calculate the position of the bubble(That thing that shows the value)
        bubblePositioning: function (answer) {
            let min = this.minValue;
            let max = this.restrictions.maxValue;
            let percentage = Number((answer.openField - min) / (max - min));
            let thumbSize = 29;

            return {
                left: `calc(${percentage * 100}% + ${(0.5 - percentage) * thumbSize}px)`,
            };
        },
        // Should the question be disabled?
        isDisabled: function (sq) {
            return !this.get.editingAllowed(sq) || this.state.global.showLoader;
        },
        niceStep(step) {
            const pow10 = Math.pow(10, Math.floor(Math.log10(step)));
            const n = step / pow10;

            let nice;

            if (n <= 1) nice = 1;
            else if (n <= 2) nice = 2;
            else if (n <= 5) nice = 5;
            else nice = 10;

            return nice * pow10;
        },
        getRangeStyle(value) {
            const min = Number(this.minValue);
            const max = Number(this.restrictions.maxValue);
            const current = Number(value);

            const percentage =
                ((current - min) / (max - min)) * 100;

            return {
                background: `linear-gradient(
                    to right,
                    var(--range-active) 0%,
                    var(--range-active) ${percentage}%,
                    var(--range-inactive) ${percentage}%,
                    var(--range-inactive) 100%
                )`
            };
        },
        rangeMouseDown: function (answer) {
            this.rangeBeingUsedId = answer.id;
            answer.isChecked = true;
        },
        rangeMouseUp: function () {
            this.rangeBeingUsedId = -1;
        },
        // Sliders have three answers. This holds the value of the slider and the label and description of the left side
        sqLeftAnswer: function (sq) {
            return sq.answers.find((a) => a.type === `Digit`);
        },
        // Sliders have three answers. This holds the value of the slider and the label and description of the right side
        sqRightAnswer: function (sq) {
            return sq.answers.find((a) => a.type === `None`);
        },
        sqAnswers: function (sq) {
            return sq.answers.filter((answer) => answer.type === `Radiobutton`);
        },
        // Sliders have three answers. This returns the outerfield
        sqOuterfield: function (sq) {
            return sq.answers.find((a) => a.type === `OuterField`);
        },
        semdiffChanged: function (answer, sq) {
            sq.answers.forEach((answer) => {
                answer.isChecked = false;
            });

            answer.isChecked = true;
        },
    },
};
</script>

<style scoped>
@import "./../styles/variables.css";

:root {
    --range-active: var(--primary-color, #3b002f);
    /* --range-inactive: var(--primary-color-25, #d8cfda); */
    --range-inactive: #E3E4E8;
}

[data-slider-container] {
    width: 100%;
}

.scale {
    position:relative;
    width: 100%;
}

/* RANGE */
.slider {
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    cursor: pointer;
    margin: 0;
    box-sizing: border-box;
}

/* TRACK */
.slider::-webkit-slider-runnable-track {
    height: 12px;
    border-radius: 999px;
    background: transparent;
}

.slider::-moz-range-track {
    height: 12px;
    border-radius: 999px;
    background: transparent;
}

/* THUMB */
.slider::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 29px;
    height: 29px;
    border-radius: 50%;
    background: var(--range-active);
    margin-top: -9px;
    cursor: pointer;
    border: 3px solid #fff;
}

.slider::-moz-range-thumb {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--range-active);
    border: none;
    cursor: pointer;
}

[data-number] {
    position: absolute;
    transform: translateX(-50%);
    color: var(--color-black-75);
    font-size: 14px;
}

.labels {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
    font-size: 16px;
    color: #666;
}

.labels label {
    width: auto !important;
}

.labels [data-left] {
    font-size: 14px;
    color: var(--color-black-70);
    text-align: left;
}

.labels [data-right] {
    font-size: 14px;
    color: var(--color-black-70);
    text-align: right;
}

[data-range-container] {
    position: relative;
    width: 100%;
}

.slider {
    position: relative;
    z-index: 2;
}

.ticks {
    position: relative;
    z-index: 1;
    pointer-events: none;
    width: calc(100% - 29px);
    margin-left: 14.5px;
    margin-top: 24px;
    height: 24px;
}

.slider-value {
    position: absolute;
    top: 24px;
    transform: translateX(-50%);
    z-index: 3;
    min-width: 36px;
    padding: 3px 8px;
    border-radius: 999px;
    background: #f1f2f4;
    color: var(--color-black-80);
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    pointer-events: none;
}

.labels {
    position: relative;
    z-index: 1;
    pointer-events: none;
}

[data-answer="Slider"] {
    margin: 30px 0;
    padding-block-start: 30px;
    flex-direction: column;
    align-items: flex-start;
    border-top: 1px solid #D9D9D9;
}

[data-answer="Slider"] [data-sq] {
    flex-direction: column;
}

[data-sq] {
    display: flex;
    justify-content: space-evenly;
    width: 100%;
}

[data-semdiff] {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
}

[data-answer="SemanticDifferential"] {
    display: flex;
    flex-direction: column;

    ~ [data-answer="SemanticDifferential"] {
        margin-top: 60px;
    }
}

[data-labels] {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 0 25px 0 25px;
}

[data-labels] label {
    overflow-wrap: break-word;
    word-break: break-word;
}

[data-left-answer] {
    display: flex;
    width: 50%;
    justify-content: flex-start;
    max-width: 200px;
    padding-left: 20px;
    text-align: left;
}

[data-right-answer] {
    display: flex;
    justify-content: flex-end;
    width: 50%;
    max-width: 200px;
    padding-right: 20px;
    text-align: right;
}

[data-outerfield-container] {
    align-self: flex-start;
}

[disabled] {
    opacity: 0.5;
    pointer-events: none;
}

[data-checkboxradio] {
    margin: 20px 0;
}

@media only screen and (max-width: 961px) {
    [data-left-answer] {
        padding-left: 0;
    }

    [data-right-answer] {
        padding-right: 0;
    }
}

@media only screen and (max-width: 550px) {
    [data-labels] {
        padding: 0 5px 0 5px;
    }
}
</style>
